import React, { MutableRefObject, ReactElement, RefObject, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@material-ui/core'
import useIsMountedRef from 'use-is-mounted-ref'
import uuid from 'uuid-random'
import {
    FormProvider,
    useForm,
    useFormContext,
    UseFormMethods
} from '../../../../Vendor/react-hook-form/dist/index.ie11'
import { PaginatedList as PaginatedListLanguage } from '../../../../../i18n'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { IsStringEmpty, isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { ON_CHANGE } from '../../Constants/HookFormEventNames'
import { ReactState } from '../../Types/Types'
import Icon, { IconName } from '../Icon'
import TextInput from '../TextInput'
import { SEARCH_FIELD_NAME } from './Constants/FieldNames'
import { CurrentEntryWrapper, PaginationCapacity } from './Types/Types'
import Loader from '../Loader'
import { debounceUnencapsulated } from '../../../../NonReact/utils/TimingUtils'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PropGenericType = Record<string, any>

type HeaderEntry = {
    classes?: string
    header: string | ReactElement
}

type OverrideLanguages = {
    NotFound?: string
}
type PropsBaseServer<T extends PropGenericType> = {
    entries: T[]
    entriesPerPage: number
    entryDisplayAsyncMapper?:
        | ((entry: T) => Promise<ReactElement>)
        | ((entry: T, index: number) => Promise<ReactElement>)
    errors?: Definable<string>[]
    isLoading?: boolean
    entryHeaders?: HeaderEntry[]
    headerContent?: ReactElement
    subHeaderContent?: ReactElement
    updateDependencies?: unknown[]
    loadingTitle?: Nullable<string>
    overrideLanguages?: OverrideLanguages
    objectSearchMutator?: undefined
    entryDisplayMapper?: undefined
    // Used for server side searching/pagination
    totalEntries?: number
    searchFunction?: (searchTerm: Nullable<string>) => void
    changePage?: (newPage?: number) => void
}

type PropsBaseSync<T extends PropGenericType> = {
    entries: T[]
    objectSearchMutator?: (object: T, index?: number) => string
    entriesPerPage: number
    entryDisplayMapper?: ((entry: T) => ReactElement) | ((entry: T, index: number) => ReactElement)
    entryDisplayAsyncMapper?: undefined
    entryHeaders?: HeaderEntry[]
    headerContent?: ReactElement
    subHeaderContent?: ReactElement
    errors?: Definable<string>[]
    isLoading?: boolean
    updateDependencies?: unknown[]
    loadingTitle?: Nullable<string>
    overrideLanguages?: OverrideLanguages
    totalEntries?: undefined
    searchFunction?: undefined
    changePage?: undefined
}

type PropsBaseAsync<T extends PropGenericType> = {
    entries: T[]
    objectSearchMutator?: (object: T, index?: number) => string
    entriesPerPage: number
    entryDisplayMapper?: undefined
    entryDisplayAsyncMapper?:
        | ((entry: T) => Promise<ReactElement>)
        | ((entry: T, index: number) => Promise<ReactElement>)
    entryHeaders?: HeaderEntry[]
    headerContent?: ReactElement
    subHeaderContent?: ReactElement
    errors?: Definable<string>[]
    isLoading?: boolean
    updateDependencies?: unknown[]
    loadingTitle?: Nullable<string>
    overrideLanguages?: OverrideLanguages
    totalEntries?: undefined
    searchFunction?: undefined
    changePage?: undefined
}

type PropsBase<T extends PropGenericType> = (PropsBaseSync<T> | PropsBaseAsync<T> | PropsBaseServer<T>) & {
    horizontalScrollGroup?: boolean
}

type Props<T extends PropGenericType> = PropsBase<T> & {
    hasForm?: boolean
    overrideLanguages?: OverrideLanguages
}

const PaginatedListNonMemo = <T extends PropGenericType>({ hasForm, ...props }: Props<T>): ReactElement => {
    const methods: UseFormMethods = useForm({
        mode: ON_CHANGE,
        reValidateMode: ON_CHANGE,
        shouldUnregister: false
    })

    const { handleSubmit }: UseFormMethods = methods

    return hasForm ? (
        <PaginatedListContent {...props} />
    ) : (
        <FormProvider {...methods}>
            <form
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onSubmit={handleSubmit((): void => {
                    // Do nothing on purpose
                })}
            >
                <PaginatedListContent {...props} />
            </form>
        </FormProvider>
    )
}

export const PaginatedList = React.memo(PaginatedListNonMemo) as typeof PaginatedListNonMemo

const MAX_NUMBER_PAGINATION_BUTTONS: number = 6

const mapCurrentEntryWrapper = <T extends unknown>(entry: T, index: number): CurrentEntryWrapper<T> => ({
    entry,
    originalIndex: index
})

export const PaginatedListContent = <T extends PropGenericType>({
    entries,
    objectSearchMutator,
    entriesPerPage,
    headerContent,
    subHeaderContent,
    entryHeaders,
    entryDisplayMapper,
    entryDisplayAsyncMapper,
    errors,
    isLoading,
    updateDependencies,
    loadingTitle,
    overrideLanguages,
    totalEntries,
    searchFunction,
    changePage,
    horizontalScrollGroup
}: PropsBase<T>): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(PaginatedListLanguage.NAME)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const paginatedListID: RefObject<string> = useRef<string>(uuid())

    const { watch, setValue }: UseFormMethods = useFormContext()

    const processedEntries: CurrentEntryWrapper<T>[] = useMemo(
        (): CurrentEntryWrapper<T>[] => entries.map(mapCurrentEntryWrapper),
        [entries]
    )

    const [currentPage, setCurrentPage]: ReactState<number> = useState<number>(0)
    const [currentEntries, setCurrentEntries]: ReactState<CurrentEntryWrapper<T>[]> =
        useState<CurrentEntryWrapper<T>[]>(processedEntries)
    const [displayedEntries, setDisplayedEntries]: ReactState<ReactElement[]> = useState<ReactElement[]>([])

    const numPaginationButtons: MutableRefObject<number> = useRef<number>(MAX_NUMBER_PAGINATION_BUTTONS)
    const paginationRange: number = Math.ceil(numPaginationButtons.current / 2)

    const rangeMin: number = entriesPerPage * currentPage
    const rangeMax: number = currentPage === 0 ? entriesPerPage : entriesPerPage * (currentPage + 1)

    const totalEntriesCount: number = totalEntries ?? entries.length
    const pageRangeBottom: number = rangeMin < totalEntriesCount ? rangeMin : totalEntriesCount
    const pageRangeTop: number = rangeMax < totalEntriesCount ? rangeMax : totalEntriesCount

    const maxNumberPages: number = Math.ceil((totalEntries ?? currentEntries.length) / entriesPerPage)
    const lastPage: number = maxNumberPages - 1

    const currentViewablePageRange = (): PaginationCapacity => {
        let minPage: number = currentPage - (paginationRange - 1)
        let maxPage: number = currentPage + paginationRange

        if (maxPage > maxNumberPages - 1) {
            minPage = maxNumberPages - numPaginationButtons.current
            maxPage = lastPage
        }

        if (minPage < 1) {
            maxPage = numPaginationButtons.current
            minPage = 1
        }

        return {
            minPage,
            maxPage
        }
    }

    const { minPage, maxPage }: PaginationCapacity = currentViewablePageRange()

    const availablePages: number[] = Array.from(Array(maxNumberPages).keys())
    const currentViewablePagesSlice: number[] = availablePages.slice(minPage, maxPage)

    const searchTerm: Nullable<string> = watch(SEARCH_FIELD_NAME)

    useEffect((): void => {
        if (IsVarEmpty(searchFunction)) {
            if (IsStringEmpty(searchTerm?.trim())) {
                setCurrentEntries((currEntries: CurrentEntryWrapper<T>[]): CurrentEntryWrapper<T>[] => {
                    if (currEntries.length !== processedEntries.length) {
                        setCurrentPage(0)
                    }

                    return processedEntries
                })
            } else {
                const filteredEntries: CurrentEntryWrapper<T>[] = processedEntries.filter(
                    ({ entry }: CurrentEntryWrapper<T>, index: number): boolean =>
                        entry &&
                        Object.values(entry).some((value: string): boolean =>
                            (!objectSearchMutator ? JSON.stringify(value) : objectSearchMutator(entry, index))
                                .toLowerCase()
                                .includes(searchTerm?.toLowerCase() ?? EMPTY_STRING)
                        )
                )

                setCurrentEntries((currEntries: CurrentEntryWrapper<T>[]): CurrentEntryWrapper<T>[] => {
                    if (currEntries.length !== filteredEntries.length) {
                        setCurrentPage(0)
                    }

                    return filteredEntries
                })
            }
        }
    }, [searchTerm, JSON.stringify(processedEntries), searchFunction])

    useEffect((): void => {
        if (isVarNotEmpty(searchFunction)) {
            setCurrentEntries(processedEntries)
        }
    }, [JSON.stringify(processedEntries), searchFunction])

    useEffect((): void => {
        if (currentEntries && isMounted.current) {
            if (entryDisplayAsyncMapper) {
                Promise.all(
                    currentEntries.map(
                        ({ entry, originalIndex }: CurrentEntryWrapper<T>): Promise<ReactElement> =>
                            entryDisplayAsyncMapper(entry, originalIndex)
                    )
                ).then((asyncEntries: ReactElement[]): void => {
                    setDisplayedEntries(asyncEntries)
                })
            } else if (entryDisplayMapper) {
                setDisplayedEntries(
                    currentEntries.map(
                        ({ entry, originalIndex }: CurrentEntryWrapper<T>): ReactElement =>
                            entryDisplayMapper(entry, originalIndex)
                    )
                )
            } else {
                setDisplayedEntries(
                    currentEntries.map(
                        ({ entry }: CurrentEntryWrapper<T>): ReactElement => (
                            <div key={uuid()}>{JSON.stringify(entry)}</div>
                        )
                    )
                )
            }
        }
    }, [JSON.stringify(currentEntries), JSON.stringify(errors), ...(updateDependencies ?? [])])

    useEffect((): void => {
        if (isVarNotEmpty(searchFunction)) {
            setCurrentPage(0)
            debounceUnencapsulated(paginatedListID.current ?? EMPTY_STRING, searchFunction, 300)(searchTerm?.trim())
        }
    }, [searchTerm])

    const hasDisplayedEntries: boolean = displayedEntries && displayedEntries.length > 0
    const showEntryHeaders: Definable<boolean> = entryHeaders && entryHeaders.length > 0 && hasDisplayedEntries

    const entryHeadersRow: Nullable<ReactElement> = showEntryHeaders ? (
        <div className='pagination-entry-headers'>
            {entryHeaders?.map(
                (headerEntry: HeaderEntry, index: number): ReactElement => (
                    <div
                        // eslint-disable-next-line @typescript-eslint/no-base-to-string
                        key={`pagination-entry-header-${headerEntry.header.toString()}-${index}`}
                        className={`pagination-entry-header ${
                            isStringNotEmpty(headerEntry?.classes) ? headerEntry.classes : EMPTY_STRING
                        }`}
                    >
                        {headerEntry.header}
                    </div>
                )
            )}
        </div>
    ) : null

    let entriesForPage: Definable<ReactElement[]>
    if (hasDisplayedEntries) {
        if (changePage) {
            entriesForPage = displayedEntries
        } else {
            entriesForPage = displayedEntries.slice(pageRangeBottom, pageRangeTop)
        }
    }

    const entriesBlock: ReactElement = (
        <div className='pagination-entries'>
            {hasDisplayedEntries ? (
                entriesForPage
            ) : (
                <div>
                    {isStringNotEmpty(overrideLanguages?.NotFound)
                        ? overrideLanguages.NotFound
                        : language.get(PaginatedListLanguage.PaginatedList_Search_NoResults)}
                </div>
            )}
        </div>
    )

    const loaderBlock: ReactElement = (
        <div className='pagination-loader'>
            <Loader />
            {isStringNotEmpty(loadingTitle) ? <div className='pl-title'>{loadingTitle}</div> : <></>}
        </div>
    )

    return (
        <div className='pagination-container'>
            <div className='pagination-header'>
                {headerContent ? <div className='pagination-header-content'>{headerContent}</div> : <></>}
                <div className='pagination-search-container'>
                    <TextInput
                        labelLeft={language.get(PaginatedListLanguage.PaginatedList_Search)}
                        inputName={SEARCH_FIELD_NAME}
                        adornmentEnd={
                            <Button
                                className='clear-search'
                                onClick={(): void => {
                                    setValue(SEARCH_FIELD_NAME, EMPTY_STRING)

                                    if (isVarNotEmpty(searchFunction)) {
                                        debounceUnencapsulated(
                                            paginatedListID.current ?? EMPTY_STRING,
                                            searchFunction,
                                            300
                                        )(EMPTY_STRING)
                                    }
                                }}
                            >
                                <Icon iconName={IconName.X} />
                            </Button>
                        }
                    />
                </div>
            </div>
            {subHeaderContent ? <div className='pagination-sub-header'>{subHeaderContent}</div> : <></>}
            <div
                className={
                    horizontalScrollGroup && showEntryHeaders ? 'pagination-headers-entries-scroll' : EMPTY_STRING
                }
            >
                {entryHeadersRow ?? <></>}
                {isLoading ? loaderBlock : entriesBlock}
            </div>
            {!isLoading && maxNumberPages > 1 ? (
                <div className='pagination-controls'>
                    <div className='prev-button'>
                        <Button
                            className='pagination-button shown-mobile'
                            disabled={currentPage === 0}
                            onClick={(): void => {
                                setCurrentPage(0)
                                changePage?.(0)
                            }}
                        >
                            <Icon iconName={IconName.FIRST_PAGE} />
                        </Button>
                        <Button
                            className='pagination-button shown-mobile'
                            disabled={currentPage === 0}
                            onClick={(): void => {
                                setCurrentPage(currentPage - 1)
                                changePage?.(currentPage - 1)
                            }}
                        >
                            <Icon iconName={IconName.CHEVRON_LEFT} />
                        </Button>
                    </div>
                    <div className='numbered-options'>
                        <Button
                            className={`pagination-button ${currentPage === 0 ? 'active shown-mobile' : EMPTY_STRING}`}
                            onClick={(): void => {
                                setCurrentPage(0)
                                changePage?.(0)
                            }}
                        >
                            1
                        </Button>
                        {currentViewablePagesSlice[0] !== 1 ? <span className='ellipsis'></span> : <></>}
                        {currentViewablePagesSlice.map(
                            (num: number): ReactElement => (
                                <Button
                                    key={num}
                                    className={`pagination-button ${
                                        num === currentPage ? 'active shown-mobile' : EMPTY_STRING
                                    }`}
                                    onClick={(): void => {
                                        setCurrentPage(num)
                                        changePage?.(num)
                                    }}
                                >
                                    {num + 1}
                                </Button>
                            )
                        )}
                        {maxNumberPages > numPaginationButtons.current ? (
                            <>
                                {currentViewablePagesSlice[currentViewablePagesSlice.length - 1] !== lastPage - 1 ? (
                                    <span className='ellipsis'></span>
                                ) : (
                                    <></>
                                )}
                                <Button
                                    className={`pagination-button ${
                                        currentPage === lastPage ? 'active shown-mobile' : EMPTY_STRING
                                    }`}
                                    onClick={(): void => {
                                        setCurrentPage(lastPage)
                                        changePage?.(lastPage)
                                    }}
                                >
                                    {maxNumberPages}
                                </Button>
                            </>
                        ) : (
                            <></>
                        )}
                    </div>
                    <div className='proceed-button-container'>
                        <Button
                            className='pagination-button shown-mobile'
                            disabled={currentPage === lastPage}
                            onClick={(): void => {
                                setCurrentPage(currentPage + 1)
                                changePage?.(currentPage + 1)
                            }}
                        >
                            <Icon iconName={IconName.CHEVRON_RIGHT} />
                        </Button>
                        <Button
                            className='pagination-button shown-mobile'
                            disabled={currentPage === lastPage}
                            onClick={(): void => {
                                setCurrentPage(lastPage)
                                changePage?.(lastPage)
                            }}
                        >
                            <Icon iconName={IconName.LAST_PAGE} />
                        </Button>
                    </div>
                </div>
            ) : (
                <></>
            )}
        </div>
    )
}
